/**
 * StoryStat — dark-background story section with an oversized stat number as the visual anchor.
 * Strong for owner-operated or tenure-heavy brands that lead with experience.
 *
 * Registered as: story / story-stat
 *
 * Dark base color defaults to --story-stat-bg (falls back to #0D1B2A).
 * Override in client theme.css: :root { --story-stat-bg: #1a1a2e; }
 */
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionComponentProps } from "../../registry.js";

gsap.registerPlugin(ScrollTrigger);

interface StoryStatProps {
  eyebrow?: string;
  headline?: string;
  body?: string | string[];
  stat?: string;
  statLabel?: string;
  badgeLabel?: string;
  badgeSubLabel?: string;
}

export function StoryStat({ props }: SectionComponentProps) {
  const {
    eyebrow = "Our Story",
    headline = "Owner-Led Operations.",
    body = "Since day one, the owner has been present at every job. No seasonal subcontractors, no shortcuts — just consistent, reliable service your property deserves.",
    stat = "6+",
    statLabel = "Years of Service",
    badgeLabel,
    badgeSubLabel,
  } = props as StoryStatProps;

  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // Split headline into per-word spans for clip reveal — runs before ScrollTrigger setup
    const h2 = el.querySelector<HTMLElement>("h2");
    if (h2) {
      const text = h2.textContent ?? "";
      h2.innerHTML = text
        .trim()
        .split(/\s+/)
        .map(
          (w) =>
            `<span style="display:inline-block;overflow:hidden;vertical-align:bottom;line-height:1.05">` +
            `<span class="ss-hword" style="display:inline-block">${w}</span></span>`,
        )
        .join(" ");
    }

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: el,
        start: "top 72%",
        once: true,
        onEnter: () => {
          const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

          // 1. Ghost number: clip-path reveal from bottom, animate to its ghost opacity
          tl.fromTo(
            ".ss-ghost-num",
            { clipPath: "inset(0 0 100% 0)", opacity: 0 },
            { clipPath: "inset(0 0 0% 0)", opacity: 0.18, duration: 1.2, ease: "power4.out" },
          )

          // 2. Stat label overlay: burst up from center
          .fromTo(
            ".ss-stat-label",
            { opacity: 0, y: 36, scale: 0.88 },
            { opacity: 1, y: 0, scale: 1, duration: 0.75 },
            "-=0.55",
          )

          // 3. Accent bar: scaleX from 0 (left origin)
          .fromTo(
            ".ss-bar",
            { scaleX: 0 },
            { scaleX: 1, duration: 0.55, ease: "power2.out", transformOrigin: "left" },
            "-=0.4",
          )

          // 4. Eyebrow label
          .fromTo(
            ".ss-eyebrow",
            { opacity: 0, y: 16 },
            { opacity: 1, y: 0, duration: 0.5 },
            "-=0.25",
          )

          // 5. Headline: per-word clip reveal (staggered)
          .fromTo(
            ".ss-hword",
            { yPercent: 115, opacity: 0 },
            { yPercent: 0, opacity: 1, duration: 0.8, stagger: 0.07 },
            "-=0.3",
          )

          // 6. Body paragraphs: stagger fade up
          .fromTo(
            ".ss-para",
            { opacity: 0, y: 22 },
            { opacity: 1, y: 0, duration: 0.65, stagger: 0.16 },
            "-=0.45",
          )

          // 7. Badge: slide up last
          .fromTo(
            ".ss-badge",
            { opacity: 0, y: 24 },
            { opacity: 1, y: 0, duration: 0.55 },
            "-=0.25",
          );
        },
      });
    }, el);

    return () => ctx.revert();
  }, []);

  const bodyParagraphs = Array.isArray(body) ? body : [body];

  return (
    <section
      ref={sectionRef}
      id="story"
      className="py-24 lg:py-32 overflow-hidden"
      style={{ background: "var(--story-stat-bg, #0D1B2A)" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-16">
        <div className="grid lg:grid-cols-2 items-center gap-16 lg:gap-24">

          {/* Stat column */}
          <div className="relative flex items-center justify-center lg:justify-start min-h-48">
            <span
              className="ss-ghost-num font-display font-black leading-none select-none"
              style={{
                fontSize: "clamp(7rem, 18vw, 18rem)",
                color: "var(--color-primary)",
                opacity: 0.18,
                letterSpacing: "-0.04em",
              }}
              aria-hidden="true"
            >
              {stat}
            </span>
            <div className="absolute inset-0 flex items-center justify-start">
              <div
                className="ss-stat-label font-display font-black uppercase leading-tight text-3xl md:text-5xl"
                style={{ color: "#fff" }}
              >
                {statLabel}
              </div>
            </div>
          </div>

          {/* Text column */}
          <div>
            <div
              className="ss-bar w-10 h-1.5 mb-8"
              style={{ background: "var(--color-primary)" }}
            />
            {eyebrow && (
              <p
                className="ss-eyebrow text-[10px] font-bold uppercase tracking-[0.3em] mb-4"
                style={{ color: "var(--color-primary)" }}
              >
                {eyebrow}
              </p>
            )}
            <h2
              className="font-display text-4xl md:text-6xl font-black uppercase mb-8 leading-tight"
              style={{ color: "#fff" }}
            >
              {headline}
            </h2>
            <div className="space-y-5 mb-10">
              {bodyParagraphs.map((p, i) => (
                <p
                  key={i}
                  className="ss-para text-lg leading-relaxed font-semibold"
                  style={{ color: "rgba(255,255,255,0.55)" }}
                >
                  {p}
                </p>
              ))}
            </div>
            {badgeLabel && (
              <div className="ss-badge flex items-center gap-5 pt-2">
                <div
                  className="w-14 h-14 border-2 flex items-center justify-center flex-shrink-0"
                  style={{ borderColor: "var(--color-primary)" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ color: "var(--color-primary)" }}
                    aria-hidden="true"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                </div>
                <div>
                  <p
                    className="font-black uppercase tracking-widest text-sm"
                    style={{ color: "#fff" }}
                  >
                    {badgeLabel}
                  </p>
                  {badgeSubLabel && (
                    <p
                      className="text-xs mt-0.5"
                      style={{ color: "rgba(255,255,255,0.4)" }}
                    >
                      {badgeSubLabel}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </section>
  );
}
